import { useMemo } from "react";
import { ArtworkCard } from "./ArtworkCard";
import { CardSkeleton, EmptyState, ErrorState, SectionHeader } from "./primitives";
import { useArtworks, useAuctions, useMarketplaceConfig } from "./useMarketplace";
import { goMarketplace, type MarketplaceRouteState } from "./mkt-routing";
import { useT } from "../../i18n";

export function ArtworksTab({ route }: { route: MarketplaceRouteState }) {
  const t = useT();
  const { data: config } = useMarketplaceConfig();
  const artworksQuery = useArtworks();
  const auctionsQuery = useAuctions();
  const artworks = useMemo(() => artworksQuery.data?.artworks ?? [], [artworksQuery.data]);
  const marketNow = auctionsQuery.data?.market_now ?? Math.floor(Date.now() / 1000);
  const live = useMemo(() => artworks.filter((a) => a.auction), [artworks]);

  const open = (id: string) => goMarketplace("artworks", route.query, { kind: "artwork", id });

  if (artworksQuery.isLoading) {
    return (
      <div data-testid="marketplace-artworks-tab" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
      </div>
    );
  }
  if (artworksQuery.isError) {
    return <ErrorState title={t("marketplace.artworks.loadErrorTitle")} detail={t("marketplace.artworks.loadErrorDetail")} onRetry={() => artworksQuery.refetch()} testId="artworks-error" />;
  }

  return (
    <div data-testid="marketplace-artworks-tab" className="space-y-12">
      {live.length > 0 && (
        <section>
          <SectionHeader index="01" title={t("marketplace.artworks.liveAuctions")} testId="artworks-auctions-header"
            action={<span className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-neon-coral">{t("marketplace.artworks.live", { n: live.length })}</span>} />
          <div data-testid="artworks-auction-grid" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {live.map((a) => <ArtworkCard key={a.artwork_id} artwork={a} marketNow={marketNow} config={config} onOpen={() => open(a.artwork_id)} />)}
          </div>
        </section>
      )}

      <section>
        <SectionHeader index={live.length > 0 ? "02" : "01"} title={t("marketplace.artworks.gallery")} testId="artworks-gallery-header"
          action={<span className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-text-muted">{t("marketplace.artworks.works", { n: artworks.length })}</span>} />
        {artworks.length === 0 ? (
          <EmptyState title={t("marketplace.artworks.emptyTitle")} hint={t("marketplace.artworks.emptyHint")} testId="artworks-empty" />
        ) : (
          <div data-testid="artworks-gallery-grid" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {artworks.map((a) => <ArtworkCard key={a.artwork_id} artwork={a} marketNow={marketNow} config={config} onOpen={() => open(a.artwork_id)} />)}
          </div>
        )}
      </section>
    </div>
  );
}
